<!-- @format -->

<template>
    <div>
        <ManageBookingCheckoutLayout :billing-details="booking.billingDetails">
            <template #left>
                <HotelReservationContainer
                    v-if="selectedOrder"
                    v-bind="propsHotelReservation"
                    :has-hotel-reservation-only="true"
                    class="mb-3 d-block d-md-none" />
            </template>
            <template #right>
                <BookingPaymentSummary
                    :total-amount="totalAmountFormatted"
                    flow="replace">
                    <template #action>
                        <div
                            v-if="totalPayLaterAmount"
                            class="gst-checkout-action__pay-later d-flex flex-row justify-center mb-1 pt-2 pb-1">
                            <div class="d-flex justify-start align-start flex-grow-1">
                                <p>
                                    {{ $t('_common:terms.payAtProperty') }}
                                </p>
                                <BaseTooltip
                                    top
                                    :text="feesExcludedText"
                                    class-content="gst-fees-container__tooltip-content"
                                    class="gst-fees-container__tooltip"
                                    class-activator="gst-checkout-action__tooltip-activator">
                                    <BaseIcon
                                        symbol-id="icons--help"
                                        :width="18"
                                        :height="18" />
                                </BaseTooltip>
                            </div>
                            <p>
                                {{ totalPayLaterAmountFormatted }}
                            </p>
                        </div>
                        <BaseFormCheckbox
                            id="agreeModel.termsEventTicket"
                            v-model="agreeModel.termsEventTicket"
                            data-test-id="manageBooking.checkbox.termsEventTicket"
                            class="mb-2">
                            <template slot="label">
                                <TermsLabel
                                    country="US"
                                    language="en"
                                    purchase-flow="HOTELS_OPEN_INVENTORY" />
                            </template>
                        </BaseFormCheckbox>
                        <div class="d-flex mb-2">
                            <BaseFormCheckbox
                                id="agreeModel.termsPrivacyPolicy"
                                v-model="agreeModel.termsPrivacyPolicy"
                                data-test-id="manageBooking.checkbox.termsPrivacyPolicy">
                                <template slot="label">
                                    {{
                                        $t(
                                            'main:views.cart.theCheckout._components.action.labels.termsPrivacyPolicy'
                                        )
                                    }}
                                    <BaseUrlLink
                                        url-link-key="privacyPolicy"
                                        i18n-key="_common:linksLabels.privacyPolicy"
                                        data-test-id="button.termsPrivacyPolicy" />
                                </template>
                            </BaseFormCheckbox>
                            <BaseTooltip
                                :text="
                                    $t(
                                        'main:views.cart.theCheckout._components.action.privacyPolicyTooltip'
                                    )
                                "
                                :open-on-click="!$vuetify.breakpoint.mdAndUp"
                                :open-on-hover="$vuetify.breakpoint.mdAndUp"
                                class-content="gst-checkout-replace-action__tooltip"
                                class-activator="gst-checkout-replace-action__tooltip-activator ml-4 ml-md-2">
                                <BaseIcon
                                    symbol-id="icons--help"
                                    :width="18"
                                    :height="18" />
                            </BaseTooltip>
                        </div>
                        <BaseButton
                            color="primary"
                            data-test-id="button.replace"
                            :disabled="!hasAgreedTerms"
                            class="gst-manage-booking-action__button u-width-100 mb-4 mt-6"
                            @click="onClickReplaceDo">
                            {{ $t('buttons.replace') }}
                        </BaseButton>
                    </template>
                </BookingPaymentSummary>
                <HotelReservationContainer
                    v-if="selectedOrder"
                    v-bind="propsHotelReservation"
                    :has-hotel-reservation-only="true"
                    class="d-none d-md-block mt-2" />
            </template>
        </ManageBookingCheckoutLayout>
    </div>
</template>
<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import zIndex from '@core/scss/mixins/_z-index-export-js.scss';
import roomRateFee from '@core/utils/constants/roomRateFee';
import TermsLabel from '@tenant/app/views/cart/TheCheckout/Action/TermsLabel';
import HotelReservationContainer from '@tenant/app/views/cart/TheCheckout/Containers/HotelReservationContainer.vue';
import ManageBookingCheckoutLayout from './_components/ManageBookingCheckoutLayout';
import BookingPaymentSummary from './_components/BookingPaymentSummary';

export default {
    name: 'ManageBookingCheckoutReplace',
    components: {
        ManageBookingCheckoutLayout,
        BookingPaymentSummary,
        HotelReservationContainer,
        TermsLabel,
    },
    props: {
        booking: {
            type: Object,
            default: () => ({}),
        },
        bookingHotel: {
            type: Object,
            default: () => ({}),
        },
        bookingHotelRoom: {
            type: Object,
            default: () => ({}),
        },
        selectedOrder: {
            type: Object,
            default: null,
        },
    },
    i18nOptions: {
        namespaces: 'main',
        keyPrefix: 'modules.hotel.features.manageBooking.theManageBookingCheckout',
    },
    data() {
        return {
            busEventProgressCircular: new Vue(),
            agreeModel: {
                termsEventTicket: false,
                termsPrivacyPolicy: false,
            },
        };
    },
    computed: {
        ...mapGetters({
            cartId: 'booking/getCartId',
            cartPurchaseFlow: `cart/purchaseFlow`,
        }),
        computedCartId() {
            return this.cartId || this.$route.params.id;
        },
        totalAmountFormatted() {
            return this.$options.filters.currencyFilter(
                this.selectedRate.totals.totalPrice,
                this.selectedRate.currency
            );
        },
        totalPayLaterAmount() {
            return this.selectedRate.totals.totalPayLaterAmount;
        },
        totalPayLaterAmountFormatted() {
            return this.$options.filters.currencyFilter(
                this.totalPayLaterAmount,
                this.selectedRate.currency
            );
        },
        feesExcluded() {
            return this.selectedRate.fees
                .map((item) => item.fee)
                .filter((item) => item.typeIncluded !== roomRateFee.TYPES_INCLUDED.INCLUDED);
        },
        feesExcludedText() {
            return this.feesExcluded
                .map((item) => this.$options.filters.roomRateFee(item, this.selectedRate.currency))
                .join(', ');
        },
        hotelReservation() {
            return this.selectedOrder?.item;
        },
        selectedRoom() {
            return this.hotelReservation?.selectedRoom;
        },
        selectedRate() {
            return this.selectedRoom?.selectedRate;
        },
        propsHotelReservation() {
            const selectedHotelRoom = {
                accommodation: this.selectedRoom.accommodation,
                hotel: {
                    name: this.hotelReservation.name,
                    image: this.hotelReservation.image,
                    address: this.hotelReservation.address,
                    policy: this.selectedRate.cancellationPolicies,
                    roomRate: this.selectedRate.totals.totalPrice,
                },
                room: {
                    name: this.selectedRoom.name,
                    rate: {
                        mealPlan: this.selectedRate.mealPlan,
                        cancellationPolicies: this.selectedRate.cancellationPolicies,
                    },
                },
                currency: this.selectedRate.currency,
            };

            return selectedHotelRoom;
        },
        hasAgreedTerms() {
            return Object.values(this.agreeModel).every((term) => term);
        },
    },
    methods: {
        ...mapActions({
            cartReplaceHotel: 'cart/replaceHotel',
            setBookingNeedsUpdate: 'booking/setNeedsUpdate',
            notificationError: 'notification/error',
        }),
        onClickReplaceDo() {
            let response;

            this.$modal.show(
                import('@tenant/app/components/modals/ProgressCircularAnimationModal.vue'),
                {
                    title: this.$t('_components.progressCircularAnimationModal.title'),
                    busEvent: this.busEventProgressCircular,
                    variant: 1,
                },
                {
                    style: `z-index: ${zIndex.highest}`,
                    'no-click-animation': true,
                    persistent: true,
                    scrollable: false,
                    fullscreen: false,
                    'hide-overlay': false,
                    'max-width': 700,
                },
                {
                    open: async () => {
                        response = await this.cartReplaceHotel({
                            cartId: this.computedCartId,
                            hotelReservation: this.$route.params.hotelReservation,
                        });
                        if (response.success) {
                            this.busEventProgressCircular.$emit('end');
                        } else {
                            this.busEventProgressCircular.$emit('close');
                        }
                    },
                    beforeClose: () => {
                        if (response?.success) {
                            this.setBookingNeedsUpdate(true);
                            this.openOrderConfirmation(response);
                        } else {
                            this.notificationError({
                                title: this.$t('messages.replaceError.title'),
                                content: this.$t('messages.replaceError.content'),
                            });
                            this.$router.replace({
                                name: 'booking',
                                params: { id: this.computedCartId },
                            });
                        }
                    },
                }
            );
        },
        openOrderConfirmation(order) {
            this.$router.replace({
                name: 'booking.confirmation',
                params: {
                    id: this.computedCartId,
                    order: order,
                    checkoutNotes: null,
                    legacyEventId: null,
                    purchaseFlow: this.cartPurchaseFlow,
                },
            });
        },
    },
    created() {
        if (!this.selectedOrder) this.$router.replace({ name: 'booking.replace' });
    },
};
</script>
<style lang="scss" scoped>
@import '@scssVariables';
@import '@scssMixins';

.gst-checkout-replace-action__pay-later {
    p {
        font-size: font-size('xxxxl');
        font-weight: font-weight('large');
    }

    .gst-checkout-replace-action__tooltip-activator {
        margin-top: 2px !important;
        margin-left: theme-spacing(2) !important;
    }
}
</style>
